/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { safeLocalStorage } from "./safeLocalStorage";
import * as Sentry from "@sentry/react";

interface CurrencyData {
  currency_code: string;
  currency_symbol: string;
  exchange_rate: number;
}

const DEFAULT_CURRENCY: CurrencyData = {
  currency_code: "USD",
  currency_symbol: "$",
  exchange_rate: 1,
};

const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

function saveCurrency(data: CurrencyData, timestamp: number): void {
  safeLocalStorage.setItem("currencyData", JSON.stringify(data));
  safeLocalStorage.setItem("currencyTimestamp", timestamp.toString());
}

export default async function CurrencyConverter(): Promise<void> {
  // DD-15: Skip entirely if currency URL is not configured — use USD default silently
  const currencyUrl = import.meta.env.VITE_REACT_APP_CURRENCY_URL;
  if (!currencyUrl) {
    saveCurrency(DEFAULT_CURRENCY, Date.now());
    return;
  }

  const storedTimestamp = safeLocalStorage.getItem("currencyTimestamp");
  const currentTimestamp = Date.now();

  if (
    storedTimestamp &&
    currentTimestamp - parseInt(storedTimestamp) < CACHE_DURATION_MS
  ) {
    return;
  }

  try {
    // DD-15: AbortController timeout — consistent with ApiClient DEFAULT_TIMEOUT (30s).
    // Without this, a stalled currency API response hangs indefinitely in the browser.
    // The 24-hour localStorage cache limits this call to once per day per user.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    let response: Response;
    try {
      response = await fetch(currencyUrl, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
    if (!response.ok) {
      throw new Error(
        `Currency API error: ${response.status} ${response.statusText}`,
      );
    }
    const responseData = await response.json();

    if (responseData.status === "success") {
      saveCurrency(
        {
          currency_code: responseData.currency_code,
          currency_symbol: responseData.currency_symbol,
          exchange_rate: responseData.exchange_rate,
        },
        currentTimestamp,
      );
      return;
    }
  } catch (error) {
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        tags: { api_method: "GET (currency)" },
      },
    );
  }

  saveCurrency(DEFAULT_CURRENCY, currentTimestamp);
}
